import { useState } from 'react';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import organizationService from '../../services/organization';

interface NewOrgModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (org: { id: number; name: string }) => void;
}

const COMMON_TIMEZONES = [
  'Australia/Melbourne',
  'Australia/Sydney',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Hobart',
  'Australia/Darwin',
  'Pacific/Auckland',
  'UTC',
];

function NewOrgModal({ open, onClose, onCreated }: NewOrgModalProps) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    timezone: 'Australia/Melbourne',
    contact_email: '',
    contact_phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    abn: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const set = <K extends keyof typeof form>(k: K, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Organisation name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '')
      ) as unknown as Parameters<typeof organizationService.create>[0];
      const response = await organizationService.create(payload);
      if (response.success) {
        // Server may return the org directly or wrapped in { organisation: ... }
        const data: any = response.data;
        const org = data?.organisation ?? data;
        if (org?.id) {
          onCreated({ id: org.id, name: org.name });
        } else {
          setError('Failed to create organisation');
        }
      } else {
        setError(response.error?.message || 'Failed to create organisation');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to create organisation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">Create New Organisation</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Name *</label>
              <input className="input-professional w-full" value={form.name}
                onChange={(e) => set('name', e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Timezone</label>
              <select className="input-professional w-full" value={form.timezone}
                onChange={(e) => set('timezone', e.target.value)}>
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Description</label>
              <textarea className="input-professional w-full" rows={2} value={form.description}
                onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Billing Email</label>
              <input type="email" className="input-professional w-full" value={form.contact_email}
                onChange={(e) => set('contact_email', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Phone</label>
              <input className="input-professional w-full" value={form.contact_phone}
                onChange={(e) => set('contact_phone', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Website</label>
              <input className="input-professional w-full" placeholder="https://" value={form.website}
                onChange={(e) => set('website', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Address Line 1</label>
              <input className="input-professional w-full" value={form.address_line1}
                onChange={(e) => set('address_line1', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Address Line 2</label>
              <input className="input-professional w-full" value={form.address_line2}
                onChange={(e) => set('address_line2', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">City</label>
              <input className="input-professional w-full" value={form.city}
                onChange={(e) => set('city', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">State</label>
              <input className="input-professional w-full" value={form.state}
                onChange={(e) => set('state', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Postcode</label>
              <input className="input-professional w-full" value={form.postcode}
                onChange={(e) => set('postcode', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Country</label>
              <input className="input-professional w-full" value={form.country}
                onChange={(e) => set('country', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-neutral-700 mb-1">ABN/ACN</label>
              <input className="input-professional w-full" value={form.abn}
                placeholder="e.g. 12 345 678 901 or ACN 123 456 789"
                onChange={(e) => set('abn', e.target.value)} />
            </div>
          </div>
          <div className="flex gap-3 pt-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={saving}>Create Organisation</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewOrgModal;
